<template>
  <div class="w-full px-5 md:px-6 lg:px-8" :style="containerStyle">
    <!-- Search bar section  -->
    <div>
      <div class="sticky z-10 w-full pb-[16px] search-bar-with-safe-area">
        <div class="flex items-center gap-3 mx-auto max-w-[896px] lg:max-w-[1280px]">
          <div
            class="rounded-[24px] py-[12px] px-[12px] bg-white flex flex-row items-center h-[48px] flex-1 shadow-sm gap-[8px] min-w-0"
          >
            <SearchIcon class="w-[24px] h-[24px] shrink-0" stroke="#373531" />
            <input
              ref="searchbar"
              v-model="searchQuery"
              type="text"
              id="search-bar"
              class="block bg-transparent min-w-0 flex-1 py-0 pr-3 text-[1rem] leading-[1.5rem] text-[#374151] placeholder:text-[#9CA3AF] focus:outline-none"
              style="height: 36px !important"
              placeholder="Search"
              @keyup.enter="hideKeyboard"
              @input="onSearch()"
            />
            <button
              class="shrink-0 cursor-pointer flex items-center justify-center w-[24px] h-[24px]"
              :class="{ invisible: !searchQuery.trim() }"
              @click="clearSearch"
            >
              <CloseIcon :fill="'#373531'" class="w-[24px] h-[24px]" />
            </button>
          </div>
          <button
            class="w-[51px] h-[36px] shrink-0 text-[#ECEBEB] text-[1rem] leading-[1.5rem] font-normal flex items-center justify-center cursor-pointer"
            @click="this.$store.commit('library/setShowSearch', false)"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    <!-- Recommended topics section -->
    <div v-if="!isSearching">
      <h1
        class="pt-[16px] pb-[16px] text-[1.25rem] leading-[1.25rem] sm:text-[1.25rem] sm:leading-[1.25rem] font-bold mb-[4px] text-[#ECEBEB]"
      >
        You may be interested in...
      </h1>
      <div class="flex items-start flex-wrap">
        <div v-for="suggestion in searchSuggestions" :key="suggestion.id">
          <div v-if="suggestion.isEmpty" class="pills" @click="useSuggestion(suggestion.name)">
            {{ suggestion.name }}
          </div>
        </div>
      </div>
    </div>
    <!-- Results section -->
    <div v-if="isSearching">
      <!-- When there's results -->
      <div v-if="searchResults.length != 0">
        <p class="text-[1.25rem] leading-[1.25rem] text-[#ECEBEB] font-bold pb-[28px] pt-[24px]">
          {{ searchResults.length > 0 ? `${searchResults.length} results` : '0 result' }}
        </p>
        <div class="flex flex-row items-center justify-between pb-5">
          <SermonResultsCard :resources="searchResults" />
        </div>
      </div>
      <!-- When 0 results -->
      <div v-if="searchResults.length === 0">
        <div class="flex items-center">
          <img src="/assets/icons/library/OopsEmoji.svg" class="w-[32px] h-[32px] pr-2 pt-1" />
          <span
            class="pt-[24px] pb-[12px] text-[1.25rem] leading-[1.25rem] sm:text-[1.25rem] sm:leading-[1.25rem] font-bold mb-[4px] text-[#ECEBEB]"
            >Oops!</span
          >
        </div>
        <p class="text-[1rem] leading-[1.5rem] pb-[32px] text-[#C0C0C0]">
          We couldn’t find what you are looking for.
        </p>
        <h1
          class="pb-[28px] text-[1.25rem] leading-[1.25rem] sm:text-[1.25rem] sm:leading-[1.25rem] font-bold mb-[4px] text-[#ECEBEB]"
        >
          You may be interested in...
        </h1>
        <div class="pills-section flex items-start flex-wrap">
          <div v-for="suggestion in searchSuggestions" :key="suggestion.id">
            <div v-if="suggestion.isNoResult" class="pills" @click="useSuggestion(suggestion.name)">
              {{ suggestion.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchIcon from '@/../public/assets/icons/library/SearchIcon.vue';
import CloseIcon from '@/../public/assets/icons/board/CloseIcon.vue';
import { mapState } from 'vuex';
import SermonResultsCard from '@/components/Library/SermonResultsCard.vue';
import { isPlatform } from '@ionic/vue';
import { Keyboard } from '@capacitor/keyboard';
import { trackLibrarySearch } from '@/utils/analytics';
import keyboardOverlap from '@/mixins/keyboard-overlap';

export default {
  mixins: [keyboardOverlap],
  components: {
    SearchIcon,
    CloseIcon,
    SermonResultsCard,
  },
  data() {
    return {
      searchQuery: '',
      isSearching: false,
      searchResults: [],
      searchDebounceTimer: null,
      // Whether iOS has been told to stop resizing the webview for the keyboard, so
      // the bridge call is made once per opening rather than on every measurement.
    };
  },
  mounted() {
    // Hide BottomNav when search is active
    document.body.classList.add('search-active');
    // Keyboard state (and the padding below) comes from the keyboard-overlap mixin;
    // it detaches on unmount.
    this.startKeyboardOverlapTracking();
  },
  beforeUnmount() {
    // Show BottomNav when search is closed
    document.body.classList.remove('search-active');
    // Clean up debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  computed: {
    ...mapState('library', ['searchSuggestions', 'searchClient']),
    // Room under the results for whatever the keyboard covers, so the last result
    // is still reachable; the bottom nav's own 80px otherwise. Sized from the
    // measured overlap — on Android the window resizes for the IME by default, and
    // reserving the reported height on top of that left a keyboard's worth of dead
    // space under the list.
    containerStyle() {
      const safeAreaBottom = 'env(safe-area-inset-bottom, 0px)';
      const paddingBottom = this.keyboardOffsetPx
        ? `calc(${this.keyboardOffsetPx}px + ${safeAreaBottom} + 16px)`
        : `calc(80px + ${safeAreaBottom})`;
      return { paddingBottom };
    },
  },
  async ionViewWillEnter() {
    // Hide BottomNav when search is active
    document.body.classList.add('search-active');
    await this.$store.dispatch('library/getAllSermons');
  },
  ionViewDidLeave() {
    // Show BottomNav when leaving search
    document.body.classList.remove('search-active');
  },
  methods: {
    // No setResizeMode() here any more. It used to ask iOS to stop resizing the webview
    // while this search was open, and set it back to 'native' on hide — which is the
    // OPPOSITE of capacitor.config.json's `resize: "none"`, and app-wide, so one search
    // changed every other screen's keyboard behaviour for the rest of the session. The
    // Android half never implemented it either (`setResizeMode` is `call.unimplemented()`).
    // The padding below is measured from whatever the webview actually does, so no mode
    // needs forcing — see the keyboard-overlap mixin.
    useSuggestion(suggestionName) {
      ((this.searchQuery = suggestionName), this.onSearch());
    },
    onSearch() {
      this.searchResults = [];
      if (this.searchQuery.trim() === '') {
        this.isSearching = false;
      } else {
        const results = this.searchClient.search(this.searchQuery);
        this.searchResults = results.map(result => result.item);
        this.isSearching = true;
        this.$store.dispatch('library/updateShowSearchResults', true);
      }
      this.trackSearch();
    },
    trackSearch() {
      // Clear previous timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }

      // Only track if search term is 2+ characters
      if (this.searchQuery.trim().length < 2) {
        return;
      }

      // Set new timer to track after 500ms of no typing
      this.searchDebounceTimer = setTimeout(() => {
        trackLibrarySearch({
          searchTerm: this.searchQuery.trim(),
          resultCount: this.searchResults.length,
          source: 'library-search',
        });
        this.searchDebounceTimer = null;
      }, 500);
    },
    hideKeyboard() {
      const isNative = isPlatform('ios') || isPlatform('android');
      if (isNative) {
        Keyboard.hide();
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.onSearch();
    },
  },
};
</script>

<style scoped>
.search-bar-with-safe-area {
  padding-top: calc(4px + env(safe-area-inset-top, 0px));
}

.pills {
  border-radius: 40px;
  border: 2px solid #ecebeb;
  color: #ecebeb;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  display: flex;
  padding: 5px 20px;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  margin-bottom: 16px;
}

.pills-section {
  /* gap: 16px; */
  flex-wrap: wrap;
}

#search-bar:focus {
  outline: none;
  box-shadow: none;
  border: none;
}

#search-bar:focus-visible {
  outline: none;
  box-shadow: none;
  border: none;
}
</style>

<style>
/* Hide the footer when search is active */
body.search-active ion-footer {
  display: none !important;
}
</style>
